import { useMemo } from 'react';

import { useService } from 'react-reservice';

import { EOrderType, IOrder, IPendingOrder } from '@aqx-core/common/api/order';
import { EColumnPreferenceOrderType } from '@aqx-core/common/api/user-account-setting';

import { useSymbolDetailId } from '../trade/analysis';
import { pickProperties } from '../utils';
import { IOrderListingContext, orderListingService } from './service';
import { IUseLivePriceParams } from './types';

export const useOrders = () => useService(orderListingService, (context) => context);

export const useOrdersWithKeys = <K extends keyof IOrderListingContext>(keys: K[]) => {
  return useService(orderListingService, (context) => {
    return pickProperties(context || {}, keys);
  });
};

export const useOrderSymbolPrices = (id: string) =>
  useService(orderListingService, (context) => {
    return context.mappedSymbolPrices[id] || {};
  });

export const useIsShowAllSymbol = () => useService(orderListingService, (context) => context.isShowAllSymbol);

export const useOrderHistoryDateRange = () =>
  useService(orderListingService, (context) => context.orderHistoryDateRange);

// Function to determine which hook to use based on EColumnPreferenceOrderType
export const useOpenPositionOrPendingOrder = (
  symbol: string,
  columnPreferenceOrderType: EColumnPreferenceOrderType
) => {
  return useService(orderListingService, (context) => {
    switch (columnPreferenceOrderType) {
      case EColumnPreferenceOrderType.OPEN_POSITION:
        return (context.mappedOpenPositions[symbol] as IOrder) || {};
      case EColumnPreferenceOrderType.PENDING_ORDER:
        return (
          (Object.values(context.mappedPendingOrders).find((item) => item.symbol === symbol) as IPendingOrder) || {}
        );
      default:
        return (context.mappedOpenPositions[symbol] as IOrder) || {};
    }
  });
};

export const useOpenPositionIds = () => useService(orderListingService, (context) => context.openPositionIds);
export const useOpenPositions = (id: string) =>
  useService(orderListingService, (context) => context.mappedOpenPositions[id] || {});

export const useOpenPositionTableDisplay = () => {
  const symbolDetailId = useSymbolDetailId();
  return useService(orderListingService, (context) => {
    const { isShowAllSymbol, openPositionsTableDisplay } = context || {};
    const filteredDisplayData = openPositionsTableDisplay.filter((order) => {
      return !isShowAllSymbol ? order.symbol === symbolDetailId : true;
    });
    return filteredDisplayData;
  });
};

export const usePendingOrderIds = () => useService(orderListingService, (context) => context.pendingOrderIds);
export const usePendingOrders = (id: string) =>
  useService(orderListingService, (context) => context.mappedPendingOrders[id] || {});

export const usePendingOrderTableDisplay = () => {
  const symbolDetailId = useSymbolDetailId();
  return useService(orderListingService, (context) => {
    const { isShowAllSymbol, pendingOrdersTableDisplay } = context || {};
    const filteredDisplayData = pendingOrdersTableDisplay.filter((order) => {
      return !isShowAllSymbol ? order.symbol === symbolDetailId : true;
    });
    return filteredDisplayData;
  });
};

export const useOrderHistoryIds = () => useService(orderListingService, (context) => context.orderHistoryIds);
export const useOrderHistories = (id: string) =>
  useService(orderListingService, (context) => context.mappedOrderHistories[id] || {});

export const useOrderHistoryTableDisplay = () => {
  const symbolDetailId = useSymbolDetailId();
  return useService(orderListingService, (context) => {
    const { isShowAllSymbol, orderHistoryTableDisplay } = context || {};
    const filteredDisplayData = orderHistoryTableDisplay.filter((order) => {
      return !isShowAllSymbol ? order.symbol === symbolDetailId : true;
    });
    return filteredDisplayData;
  });
};

export const useGetOpenPositionsAndPendingOrdersForPriceLadder = (id?: string) => {
  return useService(orderListingService, (context) => {
    const openPositions: IOrder[] = [];
    const pendingOrders: IPendingOrder[] = [];

    if (id) {
      Object.values(context.mappedOpenPositions).forEach((o) => {
        if (o.symbol === id) {
          openPositions.push(o);
        }
      });

      Object.values(context.mappedPendingOrders).forEach((o) => {
        if (o.symbol === id) {
          pendingOrders.push(o);
        }
      });
    }

    return { openPositions, pendingOrders };
  });
};

export const useOrderDetailQuery = () =>
  useService(orderListingService, (context) => ({
    data: context.orderDetail,
    isLoading: context.isOrderDetailLoading
  }));

export const useOrderLivePrice = ({ symbol, orderType, isOpenPosition, orderCurrentPrice }: IUseLivePriceParams) => {
  const { liveBuy, liveSell } = useOrderSymbolPrices(symbol);
  const { buy, sell } = orderCurrentPrice || {};

  const liveBuyOrDefault = useMemo(() => (buy !== undefined ? liveBuy || buy : liveBuy), [buy, liveBuy]);
  const liveSellOrDefault = useMemo(() => (sell !== undefined ? liveSell || sell : liveSell), [sell, liveSell]);

  const livePrice = useMemo(() => {
    switch (orderType) {
      case EOrderType.SELL:
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_LIMIT:
      case EOrderType.SELL_STOP_LIMIT:
        return isOpenPosition ? liveBuyOrDefault : liveSellOrDefault;
      default:
        return isOpenPosition ? liveSellOrDefault : liveBuyOrDefault;
    }
  }, [orderType, isOpenPosition, liveBuyOrDefault, liveSellOrDefault]);

  return livePrice;
};
